import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ApiKey } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromApiKey, toApiKeyUpdate } from '../api/mappers';
import { useUpdateApiKey } from '../hooks/use-api-key-mutations';
import {
  API_KEY_FIELDS,
  apiKeyFieldOf,
  apiKeyFormSchema,
  type ApiKeyFormValues,
} from '../schemas/api-key-form-schema';

import { ApiKeyFields } from './api-key-fields';

export interface EditApiKeyDialogProps {
  storeId: string;
  apiKey: ApiKey;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Name, origins, scopes and rate limit of a live key. Adding a storefront domain no longer means
 * minting a new key and redeploying; the key value and its kind never change.
 */
export function EditApiKeyDialog({ storeId, apiKey, open, onOpenChange }: EditApiKeyDialogProps) {
  const { t } = useTranslation(['apiKeys', 'common']);
  const message = useErrorMessage();
  const update = useUpdateApiKey(storeId);
  const form = useForm<ApiKeyFormValues>({
    resolver: useI18nResolver(apiKeyFormSchema),
    defaultValues: fromApiKey(apiKey),
  });

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ id: apiKey.id, body: toApiKeyUpdate(values) });
      close();
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: API_KEY_FIELDS,
          mapField: apiKeyFieldOf,
        })
      ) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('apiKeys:edit.title', { name: apiKey.name })}</DialogTitle>
          <DialogDescription>{t('apiKeys:edit.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <ApiKeyFields form={form} kindLocked />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
